import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { BRAND } from "../lib/brand";
import Faq from "../components/Faq";
import Reveal from "../components/Reveal";
import FaceScanner from "../components/FaceScanner";
import NotFound from "./NotFound";
import { useSeo, faqLd, breadcrumbLd, softwareLd } from "../lib/seo";

export const USE_CASES = {
  "dating-profile": {
    title: "AI Face Analysis for Your Dating Profile",
    h1: "Pick your best dating photo with AI",
    intro: "Stop guessing which selfie to lead with. Scan your photos, compare the scores, and pick the one that lands best — then follow the plan to level up.",
    points: ["Compare multiple photos objectively", "See which features stand out most", "Get grooming tips before your next shoot", "Track improvement before a big date"],
  },
  "before-and-after": {
    title: "Track Your Glow-Up: Before & After Face Analysis",
    h1: "Measure your glow-up with real numbers",
    intro: "Whether it's skincare, fitness or a new routine, Aesthetiq turns 'I think I look better' into a measurable, shareable progress chart.",
    points: ["Objective before/after scoring", "Progress charts over weeks and months", "See which metric improved most", "Shareable transformation FaceCards"],
  },
  "mewing-progress": {
    title: "Track Mewing Progress with AI Face Analysis",
    h1: "Is mewing actually working? Measure it.",
    intro: "Mewing is a long game. Aesthetiq's jawline and harmony metrics give you an objective read on whether your tongue posture is paying off.",
    points: ["Track jawline definition over time", "Consistent scoring under the same setup", "Combine with bloat-reduction tips", "Stay motivated with visible progress"],
  },
  "content-creators": {
    title: "Face Analysis for Content Creators & Streamers",
    h1: "Optimise your on-camera presence",
    intro: "Your face is your brand. Understand your strongest angles and features, and build a routine that keeps you camera-ready.",
    points: ["Know your strongest features", "Optimise lighting and angles", "Build a repeatable grooming routine", "Shareable cards for your audience"],
  },
};

export default function UseCasePage() {
  const { slug } = useParams();
  const uc = USE_CASES[slug];

  const faqs = uc ? [
    { q: "How do I get started?", a: "Upload a photo above — you'll get your full analysis in seconds, free, with no download." },
    { q: "Can I compare multiple photos?", a: `Yes. Scan each photo and compare the scores side by side in your ${BRAND.name} dashboard.` },
  ] : [];

  useSeo({
    title: uc?.title,
    description: uc?.intro,
    path: `/use-cases/${slug}`,
    jsonLd: uc ? [softwareLd, faqLd(faqs), breadcrumbLd([{ name: "Home", path: "/" }, { name: uc.h1, path: `/use-cases/${slug}` }])] : null,
  });

  if (!uc) return <NotFound />;

  return (
    <div className="container py-12">
      <div className="grid items-start gap-12 lg:grid-cols-2">
        <Reveal>
          <span className="label-eyebrow">Use case</span>
          <h1 className="mt-3 font-display text-5xl text-balance">{uc.h1}</h1>
          <p className="mt-4 text-muted-foreground">{uc.intro}</p>
          <ul className="mt-6 space-y-3">
            {uc.points.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{p}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            {Object.entries(USE_CASES).filter(([s]) => s !== slug).map(([s, u]) => (
              <Link key={s} to={`/use-cases/${s}`} className="rounded-full border border-border px-4 py-1.5 text-xs transition hover:border-primary/50">{u.h1} →</Link>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <FaceScanner />
        </Reveal>
      </div>

      <section className="mt-16 max-w-3xl">
        <h2 className="font-display text-3xl">FAQ</h2>
        <div className="mt-6"><Faq faqs={faqs} testId="usecase-faq" /></div>
      </section>
    </div>
  );
}
